import './styles.scss';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import moment from 'moment';
import { IAppState } from 'src/store';
import { ErrorService, Toastify } from 'src/services';
import { Input, View, Button, Text, TimePicker, DatePicker, Textarea } from 'src/components/common';
import './styles.scss';
import Form from 'src/components/common/Form';
import { Modal } from 'src/components/Modal';
import AddParties from './AddParties';
import { Deposition, People } from 'src/dataFactory/depositionFactory';
import InvolvedParties from './AddParties/InvolvedParties';
import { FaPlus } from 'react-icons/fa';
import { DepositionFactory } from 'src/dataFactory';
import { depositionAction } from 'src/redux/depositionRedux';
import { DateFormat, TimeFormat, getRequestFormat } from 'src/utils/momentUtils';
import { CreateDepositionPayload } from 'src/redux/depositionRedux/types';
import validationSchema from './constant';
import { validateParties } from '../helper';

export type DepositionFormValue = {
  requesterName?: string;
  name: string;
  caseName: string;
  caseId: string;
  openDate: Date;
  actualDepositionDate: Date;
  depositionTime: Date;
  caption: string;
  involvedParties: People[];
};

const NewDeposition: React.FC<Props> = ({
  onClose,
  createDeposition,
  updateDeposition,
  deposition,
  currentUser,
  initialValue,
}) => {
  const isCreate = !deposition;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isShowAddParties, setIsShowAddParties] = useState(false);
  const [partiesError, setPartiesError] = useState<string | null>(null);

  const handleOpenAddParties = () => {
    setIsShowAddParties(true);
  };
  const handleCloseModal = () => {
    setIsShowAddParties(false);
  };
  const [addedPartiesData, setAddedPartiesData] = useState<People[]>(deposition ? deposition.involvedParties : []);

  const handleSubmitForm = (data: DepositionFormValue) => {
    let date = moment(data.actualDepositionDate).format(DateFormat);
    let time = moment(data.depositionTime).format(TimeFormat);
    let actualDateTime = getRequestFormat(date + ' ' + time);
    setIsSubmitting(true);

    const involvedParties = addedPartiesData?.map(p => p.id);
    let submitData: CreateDepositionPayload = {
      caseId: data.caseId,
      actualDate: actualDateTime,
      name: data.name,
      caseName: data.caseName,
      caption: data.caption,
      openDate: getRequestFormat(data.openDate),
      involvedParties: involvedParties,
    };
    setIsSubmitting(true);
    if (isCreate) {
      createDeposition(submitData, handleCreateDepositionSuccess, handleActionFailed);
    } else {
      deposition &&
        updateDeposition({ body: submitData, uri: deposition.uri }, handleUpdateDepositionSuccess, handleActionFailed);
    }
  };
  const handleCreateDepositionSuccess = (res: any) => {
    Toastify.success('New Deposition has been created successfully!');
    onClose(false, res.id);
  };
  const handleUpdateDepositionSuccess = (res: any) => {
    Toastify.success('Deposition has been updated successfully!');
    onClose(true, { ...res, addedParties: addedPartiesData });
  };

  const getInitialData = () => {
    const initData: DepositionFormValue = {
      requesterName: deposition?.requester?.name ?? DepositionFactory.getUserName(currentUser),
      caseId: deposition?.caseId ?? initialValue?.caseId ?? '',
      name: deposition?.name ?? initialValue?.name ?? '',
      caseName: deposition?.caseName ?? initialValue?.caseName ?? '',
      openDate: deposition?.openDate ? new Date(deposition.openDate) : new Date(),
      actualDepositionDate: deposition?.depositionDate
        ? new Date(deposition.depositionDate)
        : initialValue?.actualDepositionDate
        ? new Date(initialValue.actualDepositionDate)
        : new Date(),
      depositionTime: deposition?.depositionDate
        ? new Date(deposition.depositionDate)
        : initialValue?.depositionTime
        ? new Date(initialValue.depositionTime)
        : new Date(),
      caption: deposition?.caption ?? initialValue?.caption ?? '',
      involvedParties: deposition?.involvedParties ?? [],
    };
    return initData;
  };

  const handleActionFailed = (error: any) => {
    setIsSubmitting(false);
    ErrorService.handler(error);
  };

  const handleSaveParties = (data: People[]) => {
    setAddedPartiesData([...data]);
    setIsShowAddParties(false);

    const error = validateParties(data);
    setPartiesError(error);
  };

  const handleRemoveParty = (id: string) => {
    const newParties = addedPartiesData.filter(party => party.id !== id);
    setAddedPartiesData([...newParties]);

    const error = validateParties(newParties);
    setPartiesError(error);
  };
  const isDisabledSubmit = !!partiesError || addedPartiesData.length === 0;
  return (
    <Modal onModalClosed={onClose}>
      <Text variant="title" className="mb-16" size={20}>
        {isCreate || initialValue ? 'Create a New Deposition' : 'Update Deposition'}
      </Text>

      <View className="ctn-new-deposition">
        <Formik
          initialValues={getInitialData()}
          onSubmit={handleSubmitForm}
          validationSchema={validationSchema(isCreate)}>
          {({ values, errors, touched, getFieldProps, handleSubmit, setValues, handleBlur, isValid, dirty }) => {
            return (
              <Form onSubmit={handleSubmit} autoComplete="off" className="ctn-new-deposition__form">
                <Input
                  label="Deposition ID *"
                  placeholder="Deposition ID"
                  errorMessage={touched.caption ? errors.caseId : ''}
                  containerClassName="mb-16 "
                  {...getFieldProps('caseId')}
                />
                <Input
                  label="Case Name *"
                  placeholder="Case Name"
                  errorMessage={touched.caseName ? errors.caseName : ''}
                  containerClassName="mb-16 "
                  {...getFieldProps('caseName')}
                />
                <Input
                  label="Deposition Name *"
                  placeholder="Deposition Name"
                  errorMessage={touched.name ? errors.name : ''}
                  containerClassName="mb-16 "
                  {...getFieldProps('name')}
                />
                <DatePicker
                  label="Open Date *"
                  selected={values.openDate}
                  onBlur={handleBlur}
                  onChange={(value: any) => {
                    setValues({ ...values, openDate: value });
                  }}
                  errorMessage={touched.openDate ? (errors.openDate as string) : ''}
                  containerClassName="mb-16"
                />
                <DatePicker
                  label="Actual Deposition Date *"
                  onChange={(value: Date) => {
                    setValues({ ...values, actualDepositionDate: value });
                  }}
                  selected={values.actualDepositionDate}
                  errorMessage={touched.actualDepositionDate ? (errors.actualDepositionDate as string) : ''}
                  containerClassName="mb-16"
                />
                <TimePicker
                  label="Deposition Time *"
                  selected={values.depositionTime}
                  name="timePicker"
                  onChange={(value: Date) => {
                    setValues({ ...values, depositionTime: value });
                  }}
                  errorMessage={touched.depositionTime ? (errors.depositionTime as string) : ''}
                  containerClassName="mb-16"
                />

                <Textarea
                  label="Caption"
                  placeholder="Caption"
                  containerClassName="mb-16 "
                  {...getFieldProps('caption')}
                />
                <label htmlFor={'add-parties-button'}>Involved Parties *</label>
                <Button
                  id="add-parties-button"
                  icon={<FaPlus />}
                  type="button"
                  onClick={handleOpenAddParties}
                  variant="secondary"
                  className={`${addedPartiesData ? 'mb-5' : 'mb-10'}`}
                  isLoading={isSubmitting}
                  style={{ backgroundColor: 'white', color: '#292870', border: '1px solid #292870' }}>
                  ADD PARTIES
                </Button>
                <View>
                  {isShowAddParties && (
                    <AddParties
                      onClose={handleCloseModal}
                      onSaveData={handleSaveParties}
                      addedParties={addedPartiesData}
                    />
                  )}
                  <View>
                    {addedPartiesData?.map(index => {
                      return <InvolvedParties user={index} onRemoveParty={handleRemoveParty} />;
                    })}
                  </View>
                  <Text size={14} className="has-text-danger mt-16 mb-16">
                    {partiesError}
                  </Text>
                </View>

                <View isRow justify={isCreate ? 'center' : 'flex-end'} className="mt-16">
                  {!isCreate && (
                    <Button type="button" onClick={onClose} variant="secondary-outline" isLoading={isSubmitting}>
                      CANCEL
                    </Button>
                  )}
                  <Button
                    type="submit"
                    variant="secondary"
                    className="ml-20"
                    isLoading={isSubmitting}
                    disabled={isDisabledSubmit}>
                    {isCreate || initialValue ? 'CREATE' : 'UPDATE'}
                  </Button>
                </View>
              </Form>
            );
          }}
        </Formik>
      </View>
    </Modal>
  );
};
type Props = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps & {
    deposition?: Deposition | null;
    initialValue?: {
      name?: string;
      caseName?: string;
      caseId?: string;
      openDate?: Date;
      actualDepositionDate?: Date;
      depositionTime?: Date;
      caption?: string;
      involvedParties?: People[];
    };
    onClose: (...arg: any[]) => void;
  };
const mapStateToProps = (state: IAppState) => ({
  currentUser: state.auth.user,
});
const mapDispatchToProps = {
  createDeposition: depositionAction.createDeposition,
  updateDeposition: depositionAction.updateDeposition,
};
export default connect(mapStateToProps, mapDispatchToProps)(NewDeposition);
